import {
  BulkAction,
  BulkModalBodyProps,
} from '@app/components/data-table/bulk-edit/types';
import { useBulkUpdatePurchasedGoods } from '@shared/api/hooks/purchased-goods/bulk';
import { useBulkUpdateSoldGoods } from '@shared/api/hooks/sold-goods/bulk';
import {
  IngredientPickerItem,
  OperationReportTypeEnum,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { useToast } from '@shared/components/ui/use-toast';
import { useState } from 'react';
import { InlineBaseFoodSearch } from './inline-base-food-search';
import { useOptimisticFoodResolution } from './use-optimistic-food-resolution';

type BulkUpdatePath = 'basic_food' | 'modelled_food';

const MODEL_OPTIONS: Record<
  BulkUpdatePath,
  { icon: keyof typeof Icons; label: string; description: string }
> = {
  basic_food: {
    icon: 'carrot',
    label: 'Basic food',
    description: 'Match every selected item to one food from our library.',
  },
  modelled_food: {
    icon: 'chefHat',
    label: 'Recipe',
    description: 'AI-generate a recipe per item from its name.',
  },
};

const MODEL_ORDER: BulkUpdatePath[] = ['basic_food', 'modelled_food'];

function BulkUpdateMatchedFoodModalBody({
  selectedUuids,
  selectedCount,
  onClose,
  onCompleted,
  reportType = OperationReportTypeEnum.purchase,
}: BulkModalBodyProps & { reportType?: OperationReportTypeEnum }) {
  const { toast } = useToast();
  const [path, setPath] = useState<BulkUpdatePath | null>(null);
  const [selectedBasicFood, setSelectedBasicFood] =
    useState<IngredientPickerItem | null>(null);

  const isSales = reportType === OperationReportTypeEnum.sales;
  // Both hooks are instantiated unconditionally (rules of hooks); only the one
  // matching reportType is used.
  const purchasedBulk = useBulkUpdatePurchasedGoods();
  const soldBulk = useBulkUpdateSoldGoods();

  const bulkUpdate = isSales ? soldBulk : purchasedBulk;
  const resolveRows = useOptimisticFoodResolution(reportType);

  const isLoading = bulkUpdate.isLoading;

  const handleSelect = (value: string) => {
    setPath(value as BulkUpdatePath);
    if (value !== 'basic_food') setSelectedBasicFood(null);
  };

  const canSubmit =
    !!path && (path !== 'basic_food' || !!selectedBasicFood) && !isLoading;

  const handleConfirm = async () => {
    if (!path || !canSubmit) return;
    try {
      if (path === 'basic_food') {
        await bulkUpdate.mutateAsync({
          data: {
            action: 'model-as-basic-food',
            uuids: selectedUuids,
            basicFoodUuid: selectedBasicFood?.uuid,
          },
        });
      } else {
        await bulkUpdate.mutateAsync({
          data: { action: 'model-as-recipe', uuids: selectedUuids },
        });
      }
      resolveRows(selectedUuids);
      toast({
        title:
          path === 'basic_food' ? 'Food match updated' : 'Modelling as recipes',
        description: `Applied to ${selectedCount} item${
          selectedCount === 1 ? '' : 's'
        }.`,
      });
      onCompleted();
    } catch (error) {
      toast({
        title: "Couldn't update items",
        description:
          'There was an error updating the selected items. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col gap-5 pt-2">
      <Text variant="subtle" className="text-gray-600">
        How would you like to model the {selectedCount} selected item
        {selectedCount === 1 ? '' : 's'}?
      </Text>

      <div className="flex flex-col gap-2">
        <Text variant="subSection" className="text-gray-900">
          Modelled by
        </Text>
        <Select value={path ?? undefined} onValueChange={handleSelect}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {MODEL_ORDER.map((option) => {
              const meta = MODEL_OPTIONS[option];
              const Icon = Icons[meta.icon];
              return (
                <SelectItem
                  key={option}
                  value={option}
                  icon={<Icon className="size-4 text-primary" />}
                  description={meta.description}
                >
                  {meta.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {path === 'basic_food' && (
        <div className="flex flex-col gap-2">
          <Text variant="subtle" className="text-gray-600">
            Matched food
          </Text>
          <InlineBaseFoodSearch
            selectedItem={selectedBasicFood}
            onSelect={setSelectedBasicFood}
          />
        </div>
      )}

      {path === 'modelled_food' && (
        <Text variant="subtle" className="text-gray-600">
          Our AI will generate a unique recipe (ingredients and weights) for
          each selected item based on its name and use it to model the
          item&apos;s emissions. Items where AI generation fails stay in the
          review list.
        </Text>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!canSubmit}
          loading={isLoading}
          onClick={handleConfirm}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

export const makeBulkUpdateMatchedFoodAction = ({
  reportType,
}: {
  /** Purchased vs sold bulk endpoints. Defaults to purchase. */
  reportType?: OperationReportTypeEnum;
} = {}): BulkAction => ({
  kind: 'updateMatchedFood',
  label: 'Update matched food',
  icon: 'carrot',
  variant: 'outline',
  ModalBody: (props) => (
    <BulkUpdateMatchedFoodModalBody {...props} reportType={reportType} />
  ),
});
